import { Query, keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	GetWorkflowDetailsResponseJSON,
	ListWorkflowDetailTodosResponseJSON,
	ListWorkflowTodosResponseJSON,
	ListWorkflowsResponseJSON,
	UpdateWorkflowArchivedResponseJSON,
} from "@performyard/service-contracts";

import {
	LaunchWorkflowPayload,
	LaunchWorkflowResponseJSON,
	ListWorkflowDetailTodosParams,
	ListWorkflowTodosParams,
	ListWorkflowsParams,
	SaveWorkflowTodoParams,
	WorkflowLaunchJobJSON,
	cancelWorkflow,
	checkWorkflowTodo,
	getWorkflowDetailTodos,
	getWorkflowDetails,
	getWorkflowLaunchJob,
	getWorkflowTodos,
	getWorkflows,
	launchWorkflow,
	removeWorkflowTodo,
	saveWorkflowTodo,
	updateWorkflowArchived,
} from "../../services/defs/workflows";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const workflowsKeys = {
	all: ["Workflows"] as const,
	list: (params?: ListWorkflowsParams) => [...workflowsKeys.all, "list", params] as const,
	todos: (params?: ListWorkflowTodosParams) => [...workflowsKeys.all, "todos", params] as const,
	details: (workflowId: string) => [...workflowsKeys.all, "details", workflowId] as const,
	detailTodos: (workflowId: string, params?: ListWorkflowDetailTodosParams) =>
		[...workflowsKeys.details(workflowId), "todos", params] as const,
	launchJob: (jobId: string) => [...workflowsKeys.all, "launchJob", jobId] as const,
};

export const useWorkflows = (
	params: ListWorkflowsParams = {},
	options?: PYUseQueryOptions<ListWorkflowsResponseJSON>
) => {
	return useQuery({
		queryKey: workflowsKeys.list(params),
		queryFn: () => getWorkflows(params),
		placeholderData: keepPreviousData,
		meta: {
			errorMessage: "There was an error getting the workflows.",
		},
		...options,
	});
};

export const useWorkflowTodos = (
	params: ListWorkflowTodosParams = {},
	options?: PYUseQueryOptions<ListWorkflowTodosResponseJSON>
) => {
	return useQuery({
		queryKey: workflowsKeys.todos(params),
		queryFn: () => getWorkflowTodos(params),
		placeholderData: keepPreviousData,
		meta: {
			errorMessage: "There was an error getting the to-dos.",
		},
		...options,
	});
};

export const useLaunchWorkflow = (
	options?: PYUseMutationOptions<LaunchWorkflowResponseJSON, Error, LaunchWorkflowPayload>
) => {
	return useMutation({
		mutationFn: launchWorkflow,
		meta: { disableDefaultErrorHandling: true },
		...options,
	});
};

export const WORKFLOW_LAUNCH_JOB_POLL_INTERVAL_MS = 1500;

export const useWorkflowLaunchJob = (jobId: string | null, options?: PYUseQueryOptions<WorkflowLaunchJobJSON>) => {
	return useQuery<WorkflowLaunchJobJSON>({
		queryKey: workflowsKeys.launchJob(jobId ?? ""),
		queryFn: () => getWorkflowLaunchJob(jobId as string),
		meta: { disableDefaultErrorHandling: true },
		...options,
		enabled: !!jobId && (options?.enabled ?? true),
		refetchInterval: (query: Query<WorkflowLaunchJobJSON>) =>
			query.state.data?.finished ? false : WORKFLOW_LAUNCH_JOB_POLL_INTERVAL_MS,
	});
};

export const useWorkflowDetails = (workflowId: string, options?: PYUseQueryOptions<GetWorkflowDetailsResponseJSON>) => {
	return useQuery({
		queryKey: workflowsKeys.details(workflowId),
		queryFn: () => getWorkflowDetails(workflowId),
		// The details page renders its own inline not-found/error panel, so the
		// global QueryCache toast would double-report failures here.
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
	});
};

export const useWorkflowDetailTodos = (
	workflowId: string,
	params: ListWorkflowDetailTodosParams = {},
	options?: PYUseQueryOptions<ListWorkflowDetailTodosResponseJSON>
) => {
	return useQuery({
		queryKey: workflowsKeys.detailTodos(workflowId, params),
		queryFn: () => getWorkflowDetailTodos(workflowId, params),
		placeholderData: keepPreviousData,
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
	});
};

// Every to-do/workflow mutation invalidates the whole Workflows cache: a single
// change moves status counts, list rows, and the details header at once. The
// callers own error toasts (WorkflowActionError carries the legacy handler's
// message), so the global MutationCache toast is disabled to avoid doubles.
// Spread options BEFORE onSuccess so a caller-supplied onSuccess can't silently
// replace the cache invalidation; per-mutate callbacks still run in addition.
const useWorkflowMutation = <TData, TVariables>(
	mutationFn: (variables: TVariables) => Promise<TData>,
	options?: PYUseMutationOptions<TData>
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		meta: { disableDefaultErrorHandling: true },
		...options,
		onSuccess: (...args: Parameters<NonNullable<PYUseMutationOptions<TData>["onSuccess"]>>) => {
			queryClient.invalidateQueries({ queryKey: workflowsKeys.all });
			return options?.onSuccess?.(...args);
		},
	});
};

export const useSetWorkflowArchived = (options?: PYUseMutationOptions<UpdateWorkflowArchivedResponseJSON>) =>
	useWorkflowMutation(
		({ workflowId, archived }: { workflowId: string; archived: boolean }) =>
			updateWorkflowArchived(workflowId, archived),
		options
	);

export const useCheckWorkflowTodo = (options?: PYUseMutationOptions<void>) =>
	useWorkflowMutation((params: { todoId: string; done: boolean }) => checkWorkflowTodo(params), options);

export const useSaveWorkflowTodo = (options?: PYUseMutationOptions<void>) =>
	useWorkflowMutation((params: SaveWorkflowTodoParams) => saveWorkflowTodo(params), options);

export const useRemoveWorkflowTodo = (options?: PYUseMutationOptions<void>) =>
	useWorkflowMutation((params: { todoId: string }) => removeWorkflowTodo(params), options);

export const useCancelWorkflow = (options?: PYUseMutationOptions<void>) =>
	useWorkflowMutation((params: { workflowId: string }) => cancelWorkflow(params), options);
